<template>
	<div class="sku-log-page">
		<section class="filter-card">
			<el-form inline size="small" class="filter-form" label-width="0" @submit.native.prevent="onSearch">
				<el-form-item class="keyword-item">
					<span class="keyword-label">关键字</span>
					<el-input
						v-model="keyword"
						placeholder="SKU编号、外部编码"
						clearable
						class="keyword-input"
						@keyup.enter.native="onSearch"
					/>
				</el-form-item>
				<div class="filter-actions">
					<el-button type="primary" class="btn-search" @click="onSearch">搜索</el-button>
				</div>
			</el-form>
		</section>

		<section class="table-card" ref="tableCard">
			<el-table
				v-loading="loading"
				:data="displayList"
				border
				size="small"
				class="sku-log-table"
				:height="tableHeight"
				:row-class-name="tableRowClassName"
			>
				<el-table-column label="Sku编号|外部编码" min-width="160" align="center">
					<template slot-scope="scope">
						<div class="sku-code-cell">
							<div>{{ displaySkuId(scope.row) }}</div>
							<div v-if="skuOf(scope.row).outer_id || skuOf(scope.row).out_sku_sn" class="sku-sub">
								{{ skuOf(scope.row).outer_id || skuOf(scope.row).out_sku_sn }}
							</div>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="规格/尺码" min-width="140" align="center" show-overflow-tooltip>
					<template slot-scope="scope">
						{{ skuOf(scope.row).size || skuOf(scope.row).properties_name || '-' }}
					</template>
				</el-table-column>
				<el-table-column label="推送价(元)" width="100" align="center">
					<template slot-scope="scope">{{ formatSyncPrice(scope.row) }}</template>
				</el-table-column>
				<el-table-column label="推送库存" width="80" align="center">
					<template slot-scope="scope">{{ formatNumber(scope.row.quantity) }}</template>
				</el-table-column>
				<el-table-column label="价格结果" width="88" align="center">
					<template slot-scope="scope">
						<span :class="stateClass(scope.row.price_state)">{{ formatSkuLogState(scope.row.price_state) }}</span>
					</template>
				</el-table-column>
				<el-table-column label="库存结果" width="88" align="center">
					<template slot-scope="scope">
						<span :class="stateClass(scope.row.stock_state)">{{ formatSkuLogState(scope.row.stock_state) }}</span>
					</template>
				</el-table-column>
				<el-table-column label="失败原因" min-width="220" align="center">
					<template slot-scope="scope">
						<span v-if="errorText(scope.row)" class="err-msg">{{ errorText(scope.row) }}</span>
						<span v-else>-</span>
					</template>
				</el-table-column>
				<el-table-column label="数据更新时间" min-width="150" align="center">
					<template slot-scope="scope">{{ formatTime(scope.row.data_update_time || scope.row.create_time) }}</template>
				</el-table-column>
			</el-table>
		</section>

		<section class="pager-card">
			<el-pagination
				background
				layout="total, sizes, prev, pager, next, jumper"
				:page-sizes="[10, 20, 50]"
				:current-page.sync="page"
				:page-size="pageSize"
				:total="total"
				@current-change="handleCurrentChange"
				@size-change="handleSizeChange"
			/>
		</section>
	</div>
</template>

<script>
	import {
		TB_GOOD_API,
		getRequestErrorMessage,
		stringifySkuId,
		formatSkuLogState,
		formatPriceFromFen,
		formatZeroStockError,
	} from '@/constants/taobaoShop'

	export default {
		name: 'TaobaoGoodsSkuLog',
		props: {
			productId: {
				type: [String, Number],
				default: '',
			},
		},
		data() {
			return {
				list: [],
				page: 1,
				pageSize: 10,
				total: 0,
				loading: false,
				keyword: '',
				tableHeight: 480,
			}
		},
		computed: {
			displayList() {
				const keyword = String(this.keyword || '').trim()
				if (!keyword) {
					return this.list
				}
				return this.list.filter((row) => this.matchKeyword(row, keyword))
			},
		},
		watch: {
			productId: {
				immediate: true,
				handler(id) {
					if (id) {
						this.page = 1
						this.fetchList()
					}
				},
			},
		},
		mounted() {
			this.calcTableHeight()
			window.addEventListener('resize', this.calcTableHeight)
		},
		beforeDestroy() {
			window.removeEventListener('resize', this.calcTableHeight)
		},
		methods: {
			formatSkuLogState,
			skuOf(row) {
				if (!row) {
					return {}
				}
				return row.retail_shop_product_sku || row.sku || row
			},
			displaySkuId(row) {
				const sku = this.skuOf(row)
				return stringifySkuId(sku.sku_id || row.sku_id) || '-'
			},
			matchKeyword(row, keyword) {
				const sku = this.skuOf(row)
				const text = [
					stringifySkuId(sku.sku_id || row.sku_id),
					sku.outer_id,
					sku.out_sku_sn,
				].filter(Boolean).join(' ')
				return text.indexOf(keyword) !== -1
			},
			onSearch() {
				this.page = 1
				this.fetchList()
			},
			handleCurrentChange() {
				this.fetchList()
			},
			handleSizeChange(val) {
				this.page = 1
				this.pageSize = val
				this.fetchList()
			},
			calcTableHeight() {
				this.$nextTick(() => {
					const card = this.$refs.tableCard
					if (!card) {
						return
					}
					const h = card.getBoundingClientRect().height
					this.tableHeight = h > 360 ? Math.floor(h) : 360
				})
			},
			parseListResponse(data) {
				if (!data) {
					return { list: [], count: 0 }
				}
				if (Array.isArray(data)) {
					return { list: data, count: data.length }
				}
				const rawList = data.list || data.sku_list || data.logs || data.records || []
				return {
					list: rawList,
					count: data.count != null ? data.count : (data.total_count != null ? data.total_count : (data.total || 0)),
				}
			},
			fetchList() {
				if (!this.productId) {
					this.list = []
					this.total = 0
					return
				}
				this.loading = true
				this.$axios.get(TB_GOOD_API.syncSkuLogs, {
					params: {
						retail_shop_product_id: Number(this.productId),
						page: this.page,
						limit: this.pageSize,
					},
				}).then((res) => {
					if (res.data.code == 0) {
						const parsed = this.parseListResponse(res.data.data)
						this.list = parsed.list
						this.total = parsed.count
					} else {
						this.list = []
						this.total = 0
						this.$message.error(res.data.message || res.data.msg || '获取Sku更新记录失败')
					}
				}).catch((err) => {
					console.log(err)
					this.list = []
					this.total = 0
					this.$message.error(getRequestErrorMessage(err, '获取Sku更新记录失败'))
				}).finally(() => {
					this.loading = false
					this.calcTableHeight()
				})
			},
			tableRowClassName(param) {
				if (param.row && Number(param.row.stock_state) === 3) {
					return 'sync-fail-row'
				}
				return ''
			},
			stateClass(state) {
				const text = formatSkuLogState(state)
				if (text === '成功' || text === '同步成功') {
					return 'sync-ok'
				}
				if (text === '失败' || text === '同步失败') {
					return 'sync-fail'
				}
				return ''
			},
			formatSyncPrice(row) {
				const value = row.single_price != null ? row.single_price : (row.group_price != null ? row.group_price : row.price)
				return formatPriceFromFen(value)
			},
			formatNumber(value) {
				if (value == null || value === '') {
					return '0'
				}
				return String(value)
			},
			errorText(row) {
				return formatZeroStockError(row.stock_err_msg || row.price_err_msg || row.err_msg || '')
			},
			formatTime(value) {
				if (value == null || value === '') {
					return '-'
				}
				if (typeof value === 'string' && value.indexOf('-') !== -1) {
					return this.$moment(value).format('YYYY-MM-DD HH:mm:ss')
				}
				return this.$moment.unix(Number(value)).format('YYYY-MM-DD HH:mm:ss')
			},
		},
	}
</script>

<style scoped lang="less">
	.sku-log-page {
		display: flex;
		flex-direction: column;
		gap: 8px;
		min-height: 100%;
		padding: 12px 14px 16px;
		box-sizing: border-box;
	}

	.filter-card,
	.table-card,
	.pager-card {
		background: #fff;
		border-radius: 10px;
		box-shadow: 0 2px 12px rgba(15, 23, 42, 0.06);
	}

	.filter-card {
		padding: 10px 14px 8px;
		flex-shrink: 0;
	}

	.filter-form {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
	}

	.keyword-item {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 0;
	}

	.keyword-label {
		font-size: 12px;
		color: #606266;
		white-space: nowrap;
	}

	.keyword-input {
		width: 260px;
	}

	.filter-actions {
		display: flex;
		align-items: center;
		margin-left: auto;
	}

	.btn-search {
		background: linear-gradient(135deg, #ff6b3d 0%, #ff4714 100%) !important;
		border: none !important;
	}

	.table-card {
		flex: 1;
		min-height: 0;
		padding: 10px 12px;
	}

	.sku-code-cell {
		line-height: 1.4;
		white-space: normal;
		word-break: break-all;
	}

	.sku-sub {
		color: #909399;
		font-size: 12px;
	}

	.sync-ok {
		color: #059669;
	}

	.sync-fail,
	.err-msg {
		color: #dc2626;
	}

	.err-msg {
		font-size: 12px;
		line-height: 1.4;
		word-break: break-all;
	}

	.pager-card {
		flex-shrink: 0;
		padding: 6px 12px 8px;
		display: flex;
		justify-content: flex-end;
	}
</style>

<style lang="less">
	.sku-log-page .sync-fail-row {
		background-color: #fef0f0 !important;
	}
</style>



// WEBPACK FOOTER //
// src/components/StoreConfig/taobao/goodsSkuLog.vue